/**
 * Platform detection utilities for analytics tracking
 */

import packageJson from '../../package.json';

const APP_VERSION = packageJson.version;

/**
 * Detects if the device is mobile based on user agent only.
 * Does NOT use viewport width - a narrow desktop browser window is still desktop.
 */
export const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent.toLowerCase();
  return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
};

/**
 * Detects the platform/OS
 */
export const getPlatform = (): string => {
  if (typeof navigator === 'undefined') return 'web';
  
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || '';
  
  if (/iphone|ipad|ipod/.test(userAgent) || platform.includes('iphone') || platform.includes('ipad')) {
    return 'iOS';
  }
  
  if (/android/.test(userAgent) || platform.includes('android')) {
    return 'android';
  }
  
  if (/win/.test(platform) || /windows/.test(userAgent)) {
    return 'windows';
  }
  
  if (/mac/.test(platform) || /macintosh/.test(userAgent)) {
    return 'macOS';
  }
  
  return 'web';
};

/**
 * Gets the browser user agent string
 */
export const getBrowserAgent = (): string => {
  if (typeof navigator === 'undefined') return '';
  return navigator.userAgent || '';
};

/**
 * Gets the app version
 */
export const getAppVersion = (): string => {
  return APP_VERSION;
};

/**
 * Gets the full URL including domain
 */
export const getFullUrl = (): string => {
  if (typeof window === 'undefined') return '';
  return window.location.href;
};

/**
 * Gets the URI (pathname without domain)
 * Returns path without leading slash, e.g., "events/multigroup-uiux-gathering-2026-71746012821530826513"
 */
export const getUri = (): string => {
  if (typeof window === 'undefined') return '';
  const pathname = window.location.pathname;
  // Remove leading slash if present
  const uri = pathname.startsWith('/') ? pathname.substring(1) : pathname;
  // Add search params if any
  return window.location.search ? `${uri}${window.location.search}` : uri;
};

/**
 * Gets all platform information needed for analytics
 */
export interface PlatformInfo {
  platform: string;
  appVersion: string;
  isMobile: boolean;
  agent: string;
  url: string;
  uri: string;
}

export const getPlatformInfo = (): PlatformInfo => {
  return {
    platform: getPlatform(),
    appVersion: getAppVersion(),
    isMobile: isMobileDevice(),
    agent: getBrowserAgent(),
    url: getFullUrl(),
    uri: getUri()
  };
};
